import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../store';
import { clientsService } from '../../services/clients.service';
import { ClientDetail } from '../../types/clients.types';
import { CLIENTS_PAGE_TITLE, CLIENTS_PAGE_SUBTITLE } from '../../constants/clients.constants';
import { ConfirmationModal, ConfirmationType } from '../../components/shared/ui/ConfirmationModal';
import { PAGE_LAYOUT } from '../../constants/app.constants';
import { ClientCard } from './components/ClientCard';
import { CreateClientModal } from './components/CreateClientModal';

export const ClientsPage: React.FC = () => {
  const authUser = useAppSelector((state) => state.auth.user);
  const isAdmin = authUser?.role === 'superadmin' || authUser?.role === 'admin';

  const [clients, setClients] = useState<ClientDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateClientOpen, setIsCreateClientOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<ClientDetail | null>(null);

  const fetchClients = useCallback(async (): Promise<void> => {
    try {
      const data = await clientsService.getAll();
      setClients(data);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load clients';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleCreateClient = async (name: string): Promise<void> => {
    await clientsService.create({ name });
    toast.success(`Client "${name}" created`);
    await fetchClients();
  };

  const handleDeleteClient = (clientId: string): void => {
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      setClientToDelete(client);
    }
  };

  const confirmDeleteClient = async (): Promise<void> => {
    if (!clientToDelete) return;

    try {
      await clientsService.delete(clientToDelete.id);
      toast.success(`Client "${clientToDelete.name}" deleted`);
      setClientToDelete(null);
      await fetchClients();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete client';
      toast.error(message);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-400">
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-sm">Loading clients...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={PAGE_LAYOUT.PAGE_WRAPPER}>
      <div className={PAGE_LAYOUT.WIDE_CONTAINER_NO_VERTICAL}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900">{CLIENTS_PAGE_TITLE}</h1>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-700">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Admin Only
              </span>
            </div>
            <p className="text-sm text-slate-500 mt-1">{CLIENTS_PAGE_SUBTITLE}</p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setIsCreateClientOpen(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              New Client
            </button>
          )}
        </div>

        {/* Client count */}
        <p className="text-sm text-slate-500 mb-4">
          {clients.length} client{clients.length !== 1 ? 's' : ''}
        </p>

        {/* Client list */}
        {clients.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-12 text-center">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-slate-900 mb-1">No clients yet</h3>
            <p className="text-sm text-slate-500">Create your first client to get started.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map((client) => (
              <ClientCard
                key={client.id}
                client={client}
                isAdmin={isAdmin}
                onDeleteClient={handleDeleteClient}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CreateClientModal
        isOpen={isCreateClientOpen}
        onClose={() => setIsCreateClientOpen(false)}
        onSubmit={handleCreateClient}
      />
      <ConfirmationModal
        isOpen={clientToDelete !== null}
        onClose={() => setClientToDelete(null)}
        onConfirm={confirmDeleteClient}
        type={ConfirmationType.DANGER}
        title={`Delete "${clientToDelete?.name}"?`}
        message={
          <>
            This will permanently delete <span className="font-semibold">{clientToDelete?.name}</span> and
            remove all associated lender relationships. This action cannot be undone.
          </>
        }
        confirmButtonText="Delete Client"
        cancelButtonText="Cancel"
      />
    </div>
  );
};
